/**
 * Sharing scope of a media-manager template. The value lives in
 * `media_manager_templates.access` in the database — stable identifiers; add new
 * members, do not rename existing ones.
 *
 * - `private` — a tournament's own template (`tournament_id` is set).
 * - `default` — a global preset available to every tournament. Admin-editable;
 *   `tournament_id` is always NULL for these rows. Mirrors the broadcast
 *   `templates.access = 'default'` convention (see modules/core/supabase/tables/overlay.ts).
 */
export const TemplateAccess = {
  PRIVATE: 'private',
  DEFAULT: 'default',
} as const;

export type TemplateAccess = (typeof TemplateAccess)[keyof typeof TemplateAccess];

const KNOWN_ACCESS: ReadonlySet<string> = new Set(Object.values(TemplateAccess));

export function isKnownTemplateAccess(value: string | undefined | null): value is TemplateAccess {
  return typeof value === 'string' && KNOWN_ACCESS.has(value);
}

export const DEFAULT_TEMPLATE_ACCESS: TemplateAccess = TemplateAccess.PRIVATE;

/** A `default`-access row is a global preset. */
export const isPresetTemplate = (access: TemplateAccess): boolean => access === TemplateAccess.DEFAULT;

/** Russian labels for the access selector shown to admins in the create form. */
export const TEMPLATE_ACCESS_LABELS: Readonly<Record<TemplateAccess, string>> = {
  [TemplateAccess.PRIVATE]: 'Приватный (турнир)',
  [TemplateAccess.DEFAULT]: 'Общий пресет',
};
